import { Icon, type IconName } from '@hobbytools/icons';
import type { HTMLAttributes, ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

const bannerStyles = tv({
  base: 'flex items-start gap-3 rounded-md border p-3 text-sm',
  variants: {
    tone: {
      info: 'border-info bg-info-soft text-info-fg',
      success: 'border-success bg-success-soft text-success-fg',
      warning: 'border-warning bg-warning-soft text-warning-fg',
      danger: 'border-danger bg-danger-soft text-danger-fg',
    },
  },
  defaultVariants: { tone: 'info' },
});

const icons: Record<NonNullable<VariantProps<typeof bannerStyles>['tone']>, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'alert-triangle',
  danger: 'alert-triangle',
};

export interface BannerProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof bannerStyles> {
  children: ReactNode;
}

/** Inline message. Errors and warnings are announced to assistive tech; color is paired with an icon. */
export function Banner({ tone = 'info', className, children, ...props }: BannerProps) {
  const urgent = tone === 'danger' || tone === 'warning';
  return (
    <div {...props} role={urgent ? 'alert' : 'status'} className={bannerStyles({ tone, className })}>
      <Icon name={icons[tone ?? 'info']} size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
